/**
 * When it is safe to tell a player (or just do it) that a newer build of the app has been deployed (Indra, 2026-10-04).
 *
 * A tab keeps running whatever code it first loaded — there is no service worker, and the daily "fresh first screen" only chooses
 * which SCREEN to show, not which code. So the app asks the server, now and then, for `version.json` (written by every build, see
 * vite.config.ts) and compares it with the build time baked into the code that is running. Everything here is pure, so the rules
 * are tested without a browser; `updateWatch.ts` does the fetching, the timers and the bar.
 *
 * Which screens may be interrupted is decided by a hidden marker the screen itself draws (`updateMarker`). A screen with no marker is
 * BUSY by default — a form half filled in, a scan, a lobby, a correction being reviewed — and is never prompted or reloaded.
 *  - 'idle': nothing is in progress (the first screen, the venue TV). The page reloads itself, once the player has stopped tapping for a while.
 *  - 'ok'  : reading or between rounds (the scoring screen, results, Past Games). A small bar offers a reload, only after a quiet
 *            spell, and nothing happens unless the player taps it.
 */

export type UpdateSafety = 'idle' | 'ok';
export type UpdateAction = 'none' | 'reload' | 'bar';

/** The attribute a screen's hidden marker carries. */
export const UPDATE_SAFE_ATTR = 'data-update-safe';

/** Drawn by a screen that may be updated; a screen without it is never interrupted. */
export function updateMarker(kind: UpdateSafety): string {
  return `<span ${UPDATE_SAFE_ATTR}="${kind}" hidden></span>`;
}

/** How often a visible page asks the server whether a newer build exists. */
export const UPDATE_CHECK_EVERY_MS = 5 * 60 * 1000;
/** After opening, the first check waits this long so it never competes with the screen drawing. */
export const UPDATE_FIRST_CHECK_MS = 4000;
/** An idle screen reloads once nobody has touched it for this long (15 s, Indra 2026-10-04: a player who just paused is not cut off mid-tap). */
export const IDLE_RELOAD_AFTER_MS = 15_000;
/** The bar appears on a reading screen only after this long without a tap — a table in the middle of a round is not nagged. */
export const BAR_AFTER_QUIET_MS = 45 * 1000;

/** `version.json` is `{"build": "<ISO time>"}`; anything else is ignored. */
export function parseBuild(json: unknown): string | undefined {
  if (typeof json !== 'object' || json === null) return undefined;
  const build = (json as { build?: unknown }).build;
  return typeof build === 'string' && Number.isFinite(Date.parse(build)) ? build : undefined;
}

/** True only when the deployed build is LATER than the running one — an older one (a rollback) never forces a reload. */
export function isNewerBuild(running: string, latest: string | undefined): boolean {
  if (latest === undefined) return false;
  const a = Date.parse(running);
  const b = Date.parse(latest);
  return Number.isFinite(a) && Number.isFinite(b) && b > a;
}

export function decideUpdate(input: {
  /** The deployed build is later than the running one. */
  newer: boolean;
  /** The marker on the screen now; undefined = no marker = busy. */
  safety: UpdateSafety | undefined;
  /** Milliseconds since the last tap, key or scroll. */
  quietMs: number;
  /** This page already reloaded once for this very build — reloading again would loop if the server keeps handing out the old code. */
  alreadyReloaded: boolean;
}): UpdateAction {
  if (!input.newer || input.safety === undefined) return 'none';
  if (input.safety === 'idle') {
    if (input.quietMs < IDLE_RELOAD_AFTER_MS) return 'none';
    return input.alreadyReloaded ? 'bar' : 'reload';
  }
  return input.quietMs >= BAR_AFTER_QUIET_MS ? 'bar' : 'none';
}
